import '../styles/globals.css';

import Head from 'next/head';
import Script from 'next/script';

import { Landbot } from '@/components/Landbot';
import { Layout } from '@/components/Layout';
import { ListingCardThemeProvider } from '@/lib/contexts/listing-card-theme';

import type { TypeTheme } from '@/types';
import type { AppProps } from 'next/app';

interface PageProps {
  page: {
    title: string;
    metaDescription: string;
    metaKeywords: string;
    metaRobots: string;
    hidePreFooter: boolean;
    theme: TypeTheme;
    path: string;
    preview?: any;
    attributes?: any;
    menu?: any;
    preFooter?: any;
    hasCustomFooter?: boolean;
    footerStyle?: string;
    transparentMenu?: boolean;
    stickyMenu?: boolean;
  };
}

interface MyAppProps extends AppProps<PageProps> {
  pageProps: PageProps;
}

// Sin Metatags (página nueva sin rellenar) la página NO debe salir como
// noindex,nofollow ni con título vacío: se cae al título de la página y a
// index,follow. Un Index/Follow explícito en false sigue mandando.
const metaOf = (page?: PageProps['page']) => {
  const meta = page?.attributes?.Metatags;
  return {
    title: page?.title || meta?.MetaTitle || page?.attributes?.title || '',
    description: page?.metaDescription || meta?.MetaDescription || '',
    robots: meta
      ? `${meta.Index ? 'index' : 'noindex'},${meta.Follow ? 'follow' : 'nofollow'}`
      : 'index,follow',
  };
};

const App = ({ Component, pageProps }: MyAppProps) => (
  <>
    {pageProps?.page?.preview && (
      <button
        style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}
        onClick={() => {
          location.href = `${location.origin}/api/exit-preview?slug=${location.pathname}`;
        }}
      >
        Exit Preview
      </button>
    )}

    <Head>
      <title>
        {`${metaOf(pageProps?.page).title} | ${process.env.NEXT_PUBLIC_SITE_NAME}`}
      </title>
      <meta name="description" content={metaOf(pageProps?.page).description} />
      <meta name="robots" content={metaOf(pageProps?.page).robots} />
      <link rel="icon" href="/favicon.ico" />
      {pageProps?.page?.path && (
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_FRONT_URL}${pageProps?.page?.path}`}
        />
      )}
    </Head>

    {process.env.NEXT_PUBLIC_GTM_ID &&
      process.env.NEXT_PUBLIC_GTM_ID !== 'GTM-XXXXXXX' && (
        <Script
          id="google-tag-manager"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');
              `,
          }}
        />
      )}

    {process.env.NEXT_PUBLIC_COOKIE_PRO_ID && (
      <>
        <Script id="gtag">{`
          window.dataLayer = window.dataLayer || [];
          
          function gtag(){dataLayer.push(arguments);}

          gtag('consent', 'default', {
            'ad_storage': 'denied',
            'analytics_storage': 'denied',
            'wait_for_update': 500
          });
        `}</Script>

        <Script
          src="https://cookie-cdn.cookiepro.com/scripttemplates/otSDKStub.js"
          data-domain-script={process.env.NEXT_PUBLIC_COOKIE_PRO_ID}
        />

        <Script id="OptanonWrapper">{`
          function OptanonWrapper() {}
        `}</Script>
      </>
    )}

    <ListingCardThemeProvider>
      <Layout
        theme={pageProps?.page?.theme}
        menu={pageProps?.page?.menu}
        preFooter={pageProps?.page?.preFooter}
        hasCustomFooter={pageProps?.page?.hasCustomFooter}
        footerStyle={pageProps?.page?.footerStyle}
        transparentMenu={
          pageProps?.page?.transparentMenu ??
          pageProps?.page?.attributes?.transparentMenu ??
          false
        }
        stickyMenu={pageProps?.page?.stickyMenu ?? false}
      >
        {process.env.NEXT_PUBLIC_GTM_ID &&
          process.env.NEXT_PUBLIC_GTM_ID !== 'GTM-XXXXXXX' && (
            <noscript>
              <iframe
                src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_ID}`}
                height="0"
                width="0"
                style={{ display: 'none', visibility: 'hidden' }}
              />
            </noscript>
          )}
        <Component {...pageProps} />
      </Layout>
    </ListingCardThemeProvider>

    <Landbot />
  </>
);

export default App;
